import React from "react";
import { motion } from "framer-motion";
import {
  Headset,
  PhoneCall,
  Mail,
  MessageCircle,
  ArrowLeft,
  CheckCircle2,
  ShieldCheck,
  Settings,
  Database,
  Users,
  Clock3,
  LifeBuoy,
  FileText,
  MonitorSmartphone,
  Wrench,
} from "lucide-react";
import { Link } from "react-router-dom";

const COMPANY_NAME = "SwitchIn";
const PRODUCT_NAME = "SwitchIn Jewellery Retail Software";
const SUPPORT_EMAIL = "nextloopinfotech@gmail.com";
const SUPPORT_PHONE = "+91 96996 24618";
const SUPPORT_WHATSAPP = "+91 96996 24618";
const SUPPORT_HOURS = "Monday to Saturday, 10:00 AM to 7:00 PM";
const SUPPORT_LOCATION = "Pune, Maharashtra, India";

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
});

const supportCards = [
  {
    icon: <Headset className="h-5 w-5" />,
    title: "General support",
    desc: "Billing, stock, reports, users, branches, daily operations, and common usage questions.",
    tone: "violet",
  },
  {
    icon: <Settings className="h-5 w-5" />,
    title: "Technical assistance",
    desc: "Login issue, page issue, setup issue, printing issue, configuration issue, or software error support.",
    tone: "sky",
  },
  {
    icon: <Users className="h-5 w-5" />,
    title: "Training help",
    desc: "Staff onboarding, owner usage guidance, process clarification, and workflow understanding.",
    tone: "emerald",
  },
  {
    icon: <Database className="h-5 w-5" />,
    title: "Data & setup help",
    desc: "Masters, stock setup, branch setup, user setup, import guidance, and initial configuration support.",
    tone: "amber",
  },
];

const supportSteps = [
  {
    icon: <MessageCircle className="h-5 w-5" />,
    title: "1. Share the issue",
    desc: "Tell us what screen, feature, or workflow you are facing trouble with.",
  },
  {
    icon: <LifeBuoy className="h-5 w-5" />,
    title: "2. We review it",
    desc: "Our team checks the issue, reproduces it if needed, and understands the impact.",
  },
  {
    icon: <Wrench className="h-5 w-5" />,
    title: "3. We guide or fix",
    desc: "We provide steps, remote help, configuration changes, or technical fixes where needed.",
  },
  {
    icon: <CheckCircle2 className="h-5 w-5" />,
    title: "4. Closure",
    desc: "Once resolved, we confirm the fix and help your team continue work smoothly.",
  },
];

const faqItems = [
  {
    q: "What type of support do you provide?",
    a: "We provide product guidance, technical help, onboarding assistance, configuration help, and day-to-day usage support for jewellery retail workflows.",
  },
  {
    q: "Do you help during initial setup?",
    a: "Yes. We help with onboarding, staff training, master setup, and basic operational guidance during implementation.",
  },
  {
    q: "Can I contact support on WhatsApp or phone?",
    a: "Yes. You can contact our team through call, email, or WhatsApp using the contact details provided on this page.",
  },
  {
    q: "Do you support multi-branch stores?",
    a: "Yes. We support both single-store and multi-branch setups, including user roles and branch-level workflows.",
  },
];

function getToneClasses(tone) {
  if (tone === "sky") {
    return {
      box: "bg-sky-100 text-sky-700",
      border: "hover:border-sky-200/80",
    };
  }
  if (tone === "emerald") {
    return {
      box: "bg-emerald-100 text-emerald-700",
      border: "hover:border-emerald-200/80",
    };
  }
  if (tone === "amber") {
    return {
      box: "bg-amber-100 text-amber-700",
      border: "hover:border-amber-200/80",
    };
  }

  return {
    box: "bg-violet-100 text-violet-700",
    border: "hover:border-violet-200/80",
  };
}

export default function SupportPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0f4ff] via-[#fdf9f3] to-[#e8f4fc] text-slate-900">
      {/* background */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(129,140,248,0.18),transparent),radial-gradient(ellipse_60%_40%_at_100%_100%,rgba(245,158,11,0.12),transparent),radial-gradient(ellipse_40%_30%_at_0%_100%,rgba(236,72,153,0.08),transparent)]" />
        <div className="absolute -top-32 left-1/4 h-96 w-96 rounded-full bg-[#6366f1]/20 blur-3xl" />
        <div className="absolute -bottom-32 right-1/4 h-80 w-80 rounded-full bg-[#f97316]/15 blur-3xl" />
      </div>

      <main className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-12">
        {/* back button */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp(0)}
          className="mb-6"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-violet-300 hover:text-violet-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>
        </motion.div>

        {/* hero */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeUp(0.05)}
          className="overflow-hidden rounded-[30px] border border-violet-200/70 bg-white/90 p-6 shadow-xl shadow-violet-200/30 backdrop-blur-sm md:p-8"
        >
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-violet-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-violet-700 ring-1 ring-violet-200/70">
                <Headset className="h-3.5 w-3.5" />
                Support
              </div>

              <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
                We are here to help you run your store smoothly.
              </h1>

              <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600 md:text-[15px]">
                Get help with billing, stock, reports, setup, staff usage,
                onboarding, and day-to-day operations in {PRODUCT_NAME}.
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200/70">
                  Product guidance
                </span>
                <span className="rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700 ring-1 ring-sky-200/70">
                  Technical support
                </span>
                <span className="rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-200/70">
                  Training assistance
                </span>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 text-sm text-slate-600 shadow-sm md:min-w-[290px]">
              <p className="font-semibold text-slate-800">Support info</p>
              <p className="mt-2">
                <span className="font-medium text-slate-700">Product:</span>{" "}
                {PRODUCT_NAME}
              </p>
              <p className="mt-1">
                <span className="font-medium text-slate-700">Hours:</span>{" "}
                {SUPPORT_HOURS}
              </p>
              <p className="mt-1">
                <span className="font-medium text-slate-700">Location:</span>{" "}
                {SUPPORT_LOCATION}
              </p>
            </div>
          </div>
        </motion.section>

        {/* contact cards */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeUp(0.1)}
          className="mt-6 grid gap-4 md:grid-cols-3"
        >
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="rounded-[26px] border border-slate-200/80 bg-white/95 p-5 shadow-sm transition hover:border-violet-200 hover:shadow-md"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-100 text-violet-700">
              <Mail className="h-5 w-5" />
            </div>
            <h2 className="mt-4 text-lg font-semibold text-slate-900">
              Email support
            </h2>
            <p className="mt-2 text-sm leading-7 text-slate-600">
              Share issue details, screenshots, store name, and contact number
              for faster help.
            </p>
            <p className="mt-3 text-sm font-semibold text-violet-700">
              {SUPPORT_EMAIL}
            </p>
          </a>

          <a
            href={`tel:${SUPPORT_PHONE.replace(/\s/g, "")}`}
            className="rounded-[26px] border border-slate-200/80 bg-white/95 p-5 shadow-sm transition hover:border-emerald-200 hover:shadow-md"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
              <PhoneCall className="h-5 w-5" />
            </div>
            <h2 className="mt-4 text-lg font-semibold text-slate-900">
              Call support
            </h2>
            <p className="mt-2 text-sm leading-7 text-slate-600">
              Best for urgent billing, login, operational, or day-end related
              issues.
            </p>
            <p className="mt-3 text-sm font-semibold text-emerald-700">
              {SUPPORT_PHONE}
            </p>
          </a>

          <a
            href={`https://wa.me/${SUPPORT_WHATSAPP.replace(/[^\d]/g, "")}`}
            target="_blank"
            rel="noreferrer"
            className="rounded-[26px] border border-slate-200/80 bg-white/95 p-5 shadow-sm transition hover:border-sky-200 hover:shadow-md"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-100 text-sky-700">
              <MessageCircle className="h-5 w-5" />
            </div>
            <h2 className="mt-4 text-lg font-semibold text-slate-900">
              WhatsApp support
            </h2>
            <p className="mt-2 text-sm leading-7 text-slate-600">
              Send issue text, screenshots, bill print issue, or quick support
              requests on WhatsApp.
            </p>
            <p className="mt-3 text-sm font-semibold text-sky-700">
              {SUPPORT_WHATSAPP}
            </p>
          </a>
        </motion.section>

        {/* quick summary */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeUp(0.15)}
          className="mt-6 rounded-[28px] border border-slate-200/80 bg-white/90 p-5 shadow-sm"
        >
          <h2 className="text-base font-semibold text-slate-900">
            What we can help you with
          </h2>

          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {supportCards.map((item) => {
              const tone = getToneClasses(item.tone);

              return (
                <div
                  key={item.title}
                  className={`rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm transition ${tone.border}`}
                >
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-xl ${tone.box}`}
                  >
                    {item.icon}
                  </div>
                  <h3 className="mt-3 text-sm font-semibold text-slate-900">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </motion.section>

        {/* support channels / checklist */}
        <section className="mt-6 grid gap-6 md:grid-cols-[1fr,1fr]">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp(0.2)}
            className="rounded-[28px] border border-slate-200/80 bg-white/95 p-6 shadow-sm"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-100 text-violet-700">
                <MonitorSmartphone className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Faster support tips
                </h2>
                <p className="text-sm text-slate-500">
                  These details help us resolve issues faster.
                </p>
              </div>
            </div>

            <ul className="mt-5 space-y-3 text-sm text-slate-600">
              {[
                "Share your store name and branch name.",
                "Mention the exact screen or module name.",
                "Send screenshot or screen recording if possible.",
                "Tell us whether issue is urgent, billing related, or report related.",
                "Mention when the issue started and whether all users are affected.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp(0.24)}
            className="rounded-[28px] border border-slate-200/80 bg-white/95 p-6 shadow-sm"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Support coverage
                </h2>
                <p className="text-sm text-slate-500">
                  Common support areas for stores using SwitchIn.
                </p>
              </div>
            </div>

            <div className="mt-5 grid gap-3">
              {[
                "Billing and invoice issues",
                "Stock / item / report guidance",
                "User login and access support",
                "Printer or output-related guidance",
                "Basic configuration and setup support",
                "Training and workflow clarification",
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
                >
                  <span className="text-sm font-medium text-slate-700">
                    {item}
                  </span>
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                </div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* process */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeUp(0.28)}
          className="mt-6 rounded-[28px] border border-slate-200/80 bg-white/95 p-6 shadow-sm"
        >
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-600">
              Support process
            </p>
            <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900">
              Simple support flow
            </h2>
            <p className="mt-2 text-sm leading-7 text-slate-600">
              We keep support straightforward so your store can get back to work
              quickly.
            </p>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-4">
            {supportSteps.map((step) => (
              <div
                key={step.title}
                className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
                  {step.icon}
                </div>
                <h3 className="mt-3 text-sm font-semibold text-slate-900">
                  {step.title}
                </h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* FAQ */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeUp(0.32)}
          className="mt-6"
        >
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-600">
              FAQ
            </p>
            <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900">
              Common support questions
            </h2>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            {faqItems.map((item) => (
              <div
                key={item.q}
                className="rounded-2xl border border-slate-200/80 bg-white/95 p-5 shadow-sm"
              >
                <p className="text-sm font-semibold text-slate-900">
                  {item.q}
                </p>
                <p className="mt-2 text-[13px] leading-relaxed text-slate-600">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* final CTA */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeUp(0.36)}
          className="mt-8 rounded-[30px] border border-violet-200/70 bg-gradient-to-r from-violet-50 via-white to-amber-50 p-6 shadow-sm"
        >
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <h2 className="text-xl font-semibold text-slate-900">
                Need help right now?
              </h2>
              <p className="mt-2 text-sm leading-7 text-slate-600">
                Contact our support team with your store name, issue details,
                and screenshot for faster resolution.
              </p>

              <div className="mt-4 flex items-center gap-2 text-sm text-slate-600">
                <Clock3 className="h-4 w-4 text-violet-600" />
                <span>{SUPPORT_HOURS}</span>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-black"
              >
                <Mail className="h-4 w-4" />
                Email support
              </a>

              <a
                href={`tel:${SUPPORT_PHONE.replace(/\s/g, "")}`}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-800 transition hover:border-violet-300 hover:text-violet-700"
              >
                <PhoneCall className="h-4 w-4" />
                Call now
              </a>
            </div>
          </div>
        </motion.section>
      </main>
    </div>
  );
}
